'use client';
import clsx from 'clsx';
import { ChevronLeft, MoreHorizontal } from 'lucide-react';
import { Fragment, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { useLocalforage } from '@/hooks/use-localforage';
import { usePromoCode } from '@/hooks/use-promo-code';
import { gtagEvent } from '@/utils/gtag';

import { ClickButton } from '../click-button';
import { closeHighestDrawer, Drawer } from '../drawer';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { forceAnonymousLogIn } from './quiz-actions';
import { LOCALFORAGE_KEY_QUIZ, QuizForm, scrollToQuizKey, scrollToQuizQuestion } from './quiz-form';
import { quizInitialState, QuizState } from './quiz-schema';
import { QuizStepper } from './quiz-stepper';
import {
  getFirstIncompleteStep,
  getIsStepComplete,
  getStepBlockers,
  getStepForKey,
  LAST_QUIZ_STEP,
  QUIZ_STEPS,
  QuizBlocker,
  QuizStepIndex,
} from './quiz-steps';

type StartQuizHandlerProps = {
  children: ReactNode;
  scrollToKey?: string;
  hash?: string;
};

export function StartQuizHandler({ children, scrollToKey, hash }: StartQuizHandlerProps) {
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<QuizStepIndex>(0);
  const [isResumePending, setIsResumePending] = useState(false);
  /**
   * Steps already reported this visit. Without it, Back then Continue reports the same step
   * again and the step events outnumber start_quiz, which makes a funnel built on them
   * meaningless.
   */
  const firedStepEvents = useRef(new Set<QuizStepIndex>());
  const {
    values: [quiz],
    setItem,
    store,
  } = useLocalforage<[QuizState]>([LOCALFORAGE_KEY_QUIZ]);
  const quizState = quiz ?? quizInitialState;
  const isLastStep = step === LAST_QUIZ_STEP;
  const isStepComplete = getIsStepComplete(step, quizState);
  // isStepComplete is the stricter half: quiz.isValid only asks whether the required questions
  // carry an answer, so it would happily submit an address like name@gmail..com.
  const canSubmit = !submitting && !!quiz?.isValid && !!quiz?.hasPets && isStepComplete;
  const canAdvance = isLastStep ? canSubmit : isStepComplete;
  // Submit also needs every earlier step, so the last step lists anything left behind there too.
  const blockers = (isLastStep ? QUIZ_STEPS.map((_, i) => i as QuizStepIndex) : [step]).flatMap((i) =>
    getStepBlockers(i, quizState)
  );

  const showBlocker = useCallback(
    (blocker: QuizBlocker) => {
      if (blocker.step === step) {
        scrollToQuizQuestion(blocker.question);

        return;
      }

      setStep(blocker.step);

      // The target step renders on the next frame; give it one before scrolling.
      setTimeout(() => scrollToQuizQuestion(blocker.question), 300);
    },
    [step]
  );

  const canGoTo = useCallback(
    (target: QuizStepIndex) => {
      for (let i = 0; i < target; i++) {
        if (!getIsStepComplete(i as QuizStepIndex, quizState)) return false;
      }

      return true;
    },
    [quizState]
  );

  const reportStep = useCallback((completed: QuizStepIndex) => {
    if (firedStepEvents.current.has(completed)) return;

    firedStepEvents.current.add(completed);

    gtagEvent(QUIZ_STEPS[completed].event);
  }, []);

  const goTo = useCallback(
    (target: QuizStepIndex) => {
      // Forward moves complete every step they pass, whether by Continue or by the stepper.
      for (let i = step; i < target; i++) {
        reportStep(i as QuizStepIndex);
      }

      setStep(target);
    },
    [reportStep, step]
  );

  /**
   * Opening lands on the first step that still needs something, so progress survives a refresh
   * or a close and reopen without storing the step itself. Waits for the store because the
   * `#quiz` auto-open below fires before localforage has initialized.
   */
  useEffect(() => {
    if (isResumePending && store) {
      setIsResumePending(false);

      store.getItem<QuizState>(LOCALFORAGE_KEY_QUIZ).then((quiz) => {
        const resumeStep = getFirstIncompleteStep(quiz ?? quizInitialState);
        const target = scrollToKey ? (Math.min(getStepForKey(scrollToKey), resumeStep) as QuizStepIndex) : resumeStep;

        // Steps finished on an earlier visit are not reported again. They were reported when
        // they were completed, so the funnel has to be read per user, not per session.
        firedStepEvents.current = new Set(Array.from({ length: target }, (_, i) => i as QuizStepIndex));

        setStep(target);
      });
    }
  }, [isResumePending, scrollToKey, store]);

  useEffect(() => {
    if (location.hash.slice(1) === hash) {
      const el = document.querySelector('.quiz-trigger');
      if (el) {
        const div = el as HTMLDivElement;

        div.click();
      }
    }
  }, [hash]);

  return (
    <Drawer
      triggerClassName='quiz-trigger'
      disabledFooterToggle
      header={<QuizStepper canGoTo={canGoTo} onStepClick={goTo} step={step} />}
      actions={
        <div className='flex w-full flex-col gap-2'>
          {!submitting && <StillNeeded blockers={blockers} onSelect={showBlocker} />}
          {isLastStep && <PromoReminder />}

          <div className='flex w-full items-center gap-2'>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button aria-label='More options' className='h-11 w-11 shrink-0' size='icon' variant='ghost'>
                  <MoreHorizontal className='h-5 w-5' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='start' side='top'>
                <DropdownMenuItem
                  className='min-h-11'
                  onSelect={async () => {
                    if (store) {
                      const quiz = await store?.getItem<QuizState>(LOCALFORAGE_KEY_QUIZ);

                      setItem(LOCALFORAGE_KEY_QUIZ, { ...quizInitialState, hasPets: quiz?.hasPets || false });
                    }

                    setStep(0);
                  }}
                >
                  Reset Questionnaire
                </DropdownMenuItem>
                <DropdownMenuItem
                  className='min-h-11'
                  disabled={submitting}
                  onSelect={() => {
                    closeHighestDrawer();
                  }}
                >
                  Cancel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <div className='flex-1' />

            {step > 0 && (
              <Button
                className='h-11 gap-1 pl-2'
                disabled={submitting}
                onClick={() => setStep((step - 1) as QuizStepIndex)}
                variant='outline'
              >
                <ChevronLeft className='h-4 w-4' /> Back
              </Button>
            )}

            {/*
            Not `disabled`: a disabled button swallows the tap, and the tap is exactly when we want
            to show someone which question is holding them up.
          */}
            {isLastStep ? (
              <ClickButton
                aria-disabled={!canAdvance}
                className={clsx('h-11 min-w-[8rem]', { 'opacity-50': !canAdvance })}
                clickToProgress={canAdvance}
                disabled={submitting}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  if (canAdvance) {
                    reportStep(step);

                    clickSubmitButton();
                  } else {
                    scrollToQuizKey();
                  }
                }}
              >
                Submit
              </ClickButton>
            ) : (
              <Button
                aria-disabled={!canAdvance}
                className={clsx('h-11 min-w-[8rem]', { 'opacity-50': !canAdvance })}
                onClick={() => {
                  if (canAdvance) {
                    goTo((step + 1) as QuizStepIndex);
                  } else {
                    scrollToQuizKey();
                  }
                }}
              >
                Continue
              </Button>
            )}
          </div>
        </div>
      }
      scrim
      title='Questionnaire'
      trigger={children}
      confirmClose={{
        title: 'Are you sure?',
        // Steps 1 and 2 have no Submit button, so the old copy pointed at something that was
        // not on screen.
        message: 'Your answers are saved on this device, but nothing is sent until you finish the last step.',
        confirmText: 'Leave questionnaire',
      }}
      onConfirmCloseCancel={() => {
        scrollToQuizKey();
      }}
      onOpen={() => {
        gtagEvent('start_quiz');

        setIsResumePending(true);

        forceAnonymousLogIn().then(() => {
          console.info('👤 Anonymous user logged in');
        });
      }}
    >
      <QuizForm scrollToKey={scrollToKey} setSubmitting={setSubmitting} step={step} />
    </Drawer>
  );
}

/**
 * Names what is holding up Continue or Submit, so nobody has to scroll the step looking for the
 * one unanswered question. Each entry jumps to its question. A long list (a fresh wellbeing step)
 * collapses to a count and the next one to answer.
 */
function StillNeeded({ blockers, onSelect }: { blockers: QuizBlocker[]; onSelect: (blocker: QuizBlocker) => void }) {
  if (!blockers.length) return null;

  const link = (blocker: QuizBlocker) => (
    <button className='text-left underline' onClick={() => onSelect(blocker)} type='button'>
      {truncate(blocker.label)}
    </button>
  );

  return (
    <p className='px-4 text-sm text-red-600' role='status'>
      {blockers.length > 3 ? (
        <>
          {blockers.length} answers still needed, starting with {link(blockers[0])}
        </>
      ) : (
        <>
          Still needed:{' '}
          {blockers.map((blocker, i) => (
            <Fragment key={blocker.question}>
              {i > 0 && ', '}
              {link(blocker)}
            </Fragment>
          ))}
        </>
      )}
    </p>
  );
}

function truncate(label: string) {
  return label.length > 48 ? `${label.slice(0, 45).trimEnd()}...` : label;
}

/**
 * One line of reassurance at the point of payment commitment, in the same struck-through style
 * the housing landing page uses for the pet-fee comparison.
 */
function PromoReminder() {
  const { code, pricing, hasDiscount } = usePromoCode();

  if (!hasDiscount || !code) return null;

  return (
    <p className='px-4 text-sm'>
      <span className='line-through opacity-50 mr-2'>${formatPrice(pricing.original)}</span>
      <span className='font-semibold'>${formatPrice(pricing.discounted)}</span> per pet, {code} applied
    </p>
  );
}

function formatPrice(price: number) {
  return Number.isInteger(price) ? price : price.toFixed(2);
}

function clickSubmitButton() {
  const submitButton = document.getElementById('quiz-submit') as HTMLButtonElement | null;

  submitButton?.click();
}
